/**
 * Advanced Question Bank: Domain 7 — Practical & Social Reasoning (الاستدلال العملي والاجتماعي)
 * 15 Psychometrically Rigorous Generative Template Families (Emotional Intelligence EI / Situational Judgment SJT)
 */

export const ADVANCED_SOCIAL_TEMPLATES = [
  // 1. Second-Order Theory of Mind (Nested Beliefs)
  {
    id: 'social_theory_of_mind_nested',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'يعتقد أن المفتاح لا يزال في الدرج، لأن أحمد لم يره وهو ينقله للخزانة', text_en: 'He believes the key is still in the drawer because Ahmed did not see it being moved', isCorrect: true },
        { id: 'B', text_ar: 'يعتقد أن المفتاح في الخزانة لأنه يقرأ أفكار أحمد تلقائيًا', text_en: 'He believes the key is in the closet by telepathy', isCorrect: false },
        { id: 'C', text_ar: 'يعتقد أن المفتاح ضاع في الخارج', text_en: 'He believes the key was lost outside', isCorrect: false },
        { id: 'D', text_ar: 'يعتقد أن المفتاح تم تدميره', text_en: 'He believes the key was destroyed', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في سيناريو نظرية العقل من الرتبة الثانية (Second-Order Theory of Mind):\n• وضعت "مريم" مفتاح السيارة في "الدُرج" أمام زميلها "أحمد".\n• غادر "أحمد" الغرفة لقضاء حاجة.\n• أثناء غياب أحمد، دخل "خالد" ونقل المفتاح من "الدرج" إلى "الخزانة"، وشاهدت "مريم" ذلك.\n• الآن، ما الذي تعتقده "مريم" بشأن ما يظنه "أحمد" عن مكان المفتاح عندما يعود؟`,
        questionText_en: `In a Second-Order Theory of Mind paradigm:\n• Mary placed the car key in the Drawer in front of Ahmed.\n• Ahmed left the room.\n• In Ahmed's absence, Khaled moved the key from the Drawer to the Closet, and Mary observed this.\n• What does Mary believe that Ahmed thinks regarding the location of the key upon his return?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `مريم تدرك المعرفة المعرفية لأحمد (نظام المعتقدات الزائفة False Belief): بما أن أحمد لم يشهد نقل المفتاح، فإن مريم تعلم أن أحمد لا يزال يظن أن المفتاح في مكانه الأصلي في "الدرج". الخيار الصحيح هو (${shuffled[correctIndex].id}).`,
        explanation_en: `Second-order theory of mind requires Mary to model Ahmed's mental representation: Mary correctly attributes a false belief to Ahmed, knowing he still expects the key in the drawer (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 2. Situational Judgment: Workplace Boundary Violation
  {
    id: 'social_sjt_workplace_conflict',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'الاجتماع بالزميل في جلسة مهنية خاصة، وتحديد السلوك بوضوح مع وضع حدود عمل حازمة وموثقة', text_en: 'Meet privately with the colleague, define the behavior clearly, and establish firm professional boundaries', isCorrect: true },
        { id: 'B', text_ar: 'الصراخ عليه علنًا أمام كل الموظفين في صالة العمل لإحراجه', text_en: 'Shout at him publicly in front of all staff to embarrass him', isCorrect: false },
        { id: 'C', text_ar: 'تجاهل الأمر تمامًا وتحمل الضغط على أمل أن يتوقف من تلقاء نفسه', text_en: 'Ignore the matter entirely and endure the burden passively', isCorrect: false },
        { id: 'D', text_ar: 'نشر منشورات مسيئة وتلميحات غير مباشرة على وسائل التواصل الاجتماعي', text_en: 'Post passive-aggressive hints on social media', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في اختبار الحكم الوظيفي الموقفي (Situational Judgment Test):\nيقوم زميل في فريقك باستمرار بإلقاء مهامه المتأخرة عليك دون إذن إداري مسبق، مما يهدد جودة عملك الخاص.\nما هو التصرف الأكثر نضجًا وحكمةً مهنية وفق معايير الذكاء الاجتماعي وحل النزاعات؟`,
        questionText_en: `In workplace conflict resolution and assertive communication:\nA colleague persistently dumps his overdue tasks onto your desk without administrative authorization, jeopardizing your output.\nWhat is the most mature, high-EQ professional response?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `التواصل الحازم الهادئ (Assertive Communication) في جلسة مغلقة هو الأسلوب الأمثل لترسيم الحدود المهنية دون إشعال صدام غير منتج ودون تهاون سلبي، وهو ما يطابق الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Private assertive boundary setting addresses the root cause constructively while maintaining psychological safety and accountability (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 3. Cognitive Bias Mitigation in Strategic Decisions
  {
    id: 'social_cognitive_bias_mitigation',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'مغالطة التكلفة الغارقة (Sunk Cost Fallacy)', text_en: 'Sunk Cost Fallacy', isCorrect: true },
        { id: 'B', text_ar: 'انحياز التأكيد (Confirmation Bias)', text_en: 'Confirmation Bias', isCorrect: false },
        { id: 'C', text_ar: 'أثر الهالة (Halo Effect)', text_en: 'Halo Effect', isCorrect: false },
        { id: 'D', text_ar: 'انحياز الوضع الراهن (Status Quo Bias)', text_en: 'Status Quo Bias', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `يقول مدير تنفيذي: "أنفقنا بالفعل 5 ملايين دولار على هذا المشروع الخاسر خلال السنوات الثلاث الماضية، ولذلك لا يمكننا التراجع أو إيقافه الآن حتى لو كانت كل المؤشرات المستقبلية تؤكد استمرار الخسارة!".\nأي انحياز معرفي واجتماعي يقع فيه هذا المدير تحديدًا؟`,
        questionText_en: `An executive declares: "We have already invested $5 million into this failing project over the last three years, so we cannot abort it now even if all future forecasts project continued losses!"\nWhich cognitive bias drives this flawed decision?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `"مغالطة التكلفة الغارقة" هي الاستمرار في استثمار الموارد في مشروع خاسر لمجرد تبرير النفقات السابقة التي لا يمكن استردادها على أي حال، وهو ما يحدده الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `The Sunk Cost Fallacy describes continuing an unprofitable venture based on unrecoverable past investments rather than forward-looking utility (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 4. Microexpression & Emotional Authenticity
  {
    id: 'social_microexpression_inference',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'انقباض العضلة الدويرية العينية حول العينين وظهور تجاعيد أقدام الغراب (Duchenne Marker)', text_en: 'Contraction of the orbicularis oculi producing crow\'s feet wrinkles around the eyes (Duchenne Marker)', isCorrect: true },
        { id: 'B', text_ar: 'سحب زاويتي الفم للخلف فقط بواسطة العضلة الضحكية دون أي حركة للعينين', text_en: 'Retracting mouth corners only without any orbital eye contraction', isCorrect: false },
        { id: 'C', text_ar: 'اتساع حدقة العين وتحديق ثابت دون رمش', text_en: 'Pupillary dilation with fixed unblinking stare', isCorrect: false },
        { id: 'D', text_ar: 'عض الشفة السفلية ورفع الحاجب الأيمن فقط', text_en: 'Biting the lower lip while raising one eyebrow', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في دراسات لغة الجسد وعلم النفس العصبي للتعابير الوجهية الدقيقة (بول إيكمان Paul Ekman):\nما هي العلامة التشريحية الإلزامية غير الإرادية التي تميز "الابتسامة الصادقة الحقيقية (Duchenne Smile)" عن الابتسامة الاجتماعية المصطنعة المزيفة؟`,
        questionText_en: `In affective neuroscience and microexpression research (Paul Ekman):\nWhat is the hallmark involuntary anatomical sign distinguishing an authentic Duchenne Smile from an artificial social smile?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `الابتسامة الصادقة لا تقتصر على الفم، بل تتطلب حركة غير إرادية للعضلة الدائرية حول العين (Orbicularis Oculi) مما يولد تجاعيد حول زوايا العينين، وهو المعيار العلمي للابتسامة الصادقة في (${shuffled[correctIndex].id}).`,
        explanation_en: `Involuntary contraction of the orbicularis oculi (Duchenne marker) crinkles the outer eye corners, which cannot be consciously faked in artificial social smiles (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 5. Strategic Negotiation: BATNA Application
  {
    id: 'social_negotiation_batna',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'البديل الأفضل في حال فشل المفاوضات وعدم التوصل لاتفاق (Best Alternative to a Negotiated Agreement)', text_en: 'The Best Alternative to a Negotiated Agreement in case talks collapse', isCorrect: true },
        { id: 'B', text_ar: 'أعلى سعر خيالي يمكن طلبه من الطرف الآخر لإرباكه', text_en: 'The highest unrealistic opening offer to anchor the counterparty', isCorrect: false },
        { id: 'C', text_ar: 'التنازل التام عن جميع شروطك لكسب رضا الطرف الآخر', text_en: 'Complete capitulation to appease the counterparty', isCorrect: false },
        { id: 'D', text_ar: 'التهديد باللجوء إلى القضاء الفوري قبل بدء النقاش', text_en: 'Threatening immediate litigation before discussions begin', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في نظرية التفاوض الاستراتيجي المعاصر بمدرسة هارفارد:\nماذا يمثل مفهوم الـ (BATNA) بدقة، ولماذا يُعتبر مصدر القوة الأعظم لأي مفاوض على طاولة الحوار؟`,
        questionText_en: `In Harvard Strategic Negotiation Theory:\nWhat exactly is a party's BATNA, and why does it constitute the single greatest source of negotiating leverage?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `الـ BATNA هو "الخيار البديل الأفضل المتاح لك إذا انهارت المفاوضات". وجود بديل قوي يحميك من قبول شروط مجحفة ويمنحك قوة الرفض والانسحاب بثقة، وهو ما يطابق الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `BATNA defines your fallback power: having a strong alternative outside the room prevents you from accepting suboptimal terms, establishing (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 6. Groupthink Mitigation: Devil's Advocate
  {
    id: 'social_groupthink_prevention',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'تعيين عضو محدد رسميًا ليلعب دور "محامي الشيطان" لنقد الخطة واستخراج نقاط الضعف دون حرج', text_en: 'Formally assign a "Devil\'s Advocate" to challenge assumptions and identify vulnerabilities without fear', isCorrect: true },
        { id: 'B', text_ar: 'إسكات أي صوت معارض ومعاقبته لضمان وحدة الصف والانسجام الشكلي', text_en: 'Silence dissenting voices to enforce superficial harmony', isCorrect: false },
        { id: 'C', text_ar: 'اتخاذ القرارات بالتصويت السريع في أول 5 دقائق لتوفير الوقت', text_en: 'Rush to a snap vote in the first 5 minutes', isCorrect: false },
        { id: 'D', text_ar: 'منع أعضاء الفريق من الاطلاع على أي تقارير أو بيانات خارجية', text_en: 'Bar team members from reviewing external independent data', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في سيكولوجيا القيادة الجماعية (Groupthink Prevention):\nعندما يميل أعضاء الفريق إلى الإجماع السريع المتسرع وتفادي الاختلاف خوفًا من الصدام، ما هو الإجراء المؤسسي الأكثر فعالية لحماية الفريق من متلازمة "تفكير الجماعة"؟`,
        questionText_en: `In organizational psychology and Groupthink mitigation:\nWhen a committee rushes into premature unanimity to avoid friction, what institutional practice best safeguards against flawed groupthink?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `مأسسة دور "محامي الشيطان" (Devil's Advocacy) تمنح رخصة رسمية مشروعة لنقد الافتراضات واختبار صلابة القرارات دون أن يُنظر للنقد على أنه عداء شخصي، وهو ما يثبت صحة الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Institutionalizing the Devil's Advocate role normalizes cognitive friction and stress-tests decisions without interpersonal animosity (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 7. Active Listening & Psychotherapeutic Validation
  {
    id: 'social_active_listening_reframing',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: '"أنا أسمعك جيدًا، ويبدو أنك تشعر بضغط هائل وعجز بسبب هذه المتطلبات المتراكمة. هل هذا ما تمره به؟"', text_en: '"I hear you, and it sounds like you are feeling overwhelmed and exhausted by these compounding demands. Is that what you are experiencing?"', isCorrect: true },
        { id: 'B', text_ar: '"أنت تبالغ كعادتك والأمر لا يستحق كل هذا البكاء، اهدأ فورًا!"', text_en: '"You are overreacting as usual; calm down immediately!"', isCorrect: false },
        { id: 'C', text_ar: '"أنت لا تعرف شيئًا عن الضغط الحقيقي، يجب أن ترى جدولي المزدحم لتعرف المعاناة!"', text_en: '"You know nothing of real stress; look at my schedule to see true suffering!"', isCorrect: false },
        { id: 'D', text_ar: '"لا تشغل بالك، اذهب وشاهد فيلمًا وسينتهي كل شيء من تلقاء نفسه."', text_en: '"Never mind, go watch a movie and it will magically resolve itself."', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `صديق لك يمر بأزمة حادة ويقول لك وهو في قمة التوتر والانفعال: "أشعر أنني غارق تمامًا ولا أستطيع مجاراة أي شيء في العمل أو الحياة!".\nما هي الاستجابة التي تمثل قمة "الإنصات النشط والتحقق العاطفي (Active Listening & Emotional Validation)"؟`,
        questionText_en: `A stressed colleague confides: "I feel completely overwhelmed and unable to keep up with work or life demands!"\nWhich response exemplifies masterclass Active Listening and Emotional Validation?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `الإنصات النشط يقوم على إعادة صياغة المشاعر والتسمية الدقيقة لشعور الطرف الآخر (Naming and Reframing) دون إطلاق أحكام أو تقديم نصائح سريعة غير مطلوبة، وهو ما يمثله الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Reframing and validating affective states without premature dismissal or unsolicited advice builds therapeutic safety and trust (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 8. Fundamental Attribution Error (FAE)
  {
    id: 'social_attribution_error',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'خطأ العزو الأساسي (Fundamental Attribution Error)', text_en: 'Fundamental Attribution Error', isCorrect: true },
        { id: 'B', text_ar: 'أثر التباين الحسي (Contrast Effect)', text_en: 'Contrast Effect', isCorrect: false },
        { id: 'C', text_ar: 'انحياز الحداثة (Recency Bias)', text_en: 'Recency Bias', isCorrect: false },
        { id: 'D', text_ar: 'النبؤة المحققة لذاتها (Self-Fulfilling Prophecy)', text_en: 'Self-Fulfilling Prophecy', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `تأخر زميل عن الاجتماع الصباحي، فقال المدير فورًا: "إنه شخص مهمل وفاشل بطبعه"، متجاهلاً وجود حادث مروري خانق أغلق الطريق السريع بالكامل.\nما هو التحيز النفسي الاجتماعي الذي وقع فيه المدير هنا؟`,
        questionText_en: `A peer arrives late to a meeting, and the manager instantly mutters: "He is fundamentally lazy and irresponsible," ignoring an emergency highway closure.\nWhich social-cognitive bias did the manager exhibit?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `"خطأ العزو الأساسي" هو الميل البشري لتفسير سلوك الآخرين بإرجاعه إلى سماتهم وشخصيتهم الداخلية مع التقليل المفرط من تأثير الظروف البيئية القاهرة، وهو ما يطابق الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `The Fundamental Attribution Error overemphasizes internal dispositional traits while disregarding external situational factors (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 9. Repeated Prisoner's Dilemma & Tit-for-Tat
  {
    id: 'social_trust_game_reciprocity',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'استراتيجية المعاملة بالمثل المشروطة (Tit-for-Tat: ابدأ بالتعاون، ثم كرّر في كل جولة ما فعله الخصم بالضبط)', text_en: 'Tit-for-Tat: Start cooperative, then mirror the counterparty\'s previous move exactly', isCorrect: true },
        { id: 'B', text_ar: 'الخيانة الدائمة في كل جولة مهما فعل الخصم', text_en: 'Always defect in every round regardless of behavior', isCorrect: false },
        { id: 'C', text_ar: 'التعاون الأعمى الدائم حتى لو تعرضت للخيانة والاستغلال باستمرار', text_en: 'Always cooperate blindly even when exploited', isCorrect: false },
        { id: 'D', text_ar: 'الانسحاب من اللعبة بعد الجولة الأولى', text_en: 'Quit the game after round 1', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في نظرية الألعاب وسيكولوجيا الثقة والتعاون (معضلة السجين المتكررة Iterated Prisoner's Dilemma):\nما هي الاستراتيجية السلوكية الرياضية التي أثبتت أبحاث روبرت أكسلرود أنها الأكثر نجاحًا وبقاءً لبناء الثقة والردع في نفس الوقت؟`,
        questionText_en: `In game theory and social trust dynamics (Iterated Prisoner's Dilemma):\nWhich behavioral strategy was mathematically proven by Robert Axelrod to achieve maximum collective payoff and stability?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `استراتيجية "واحدة بواحدة" (Tit-for-Tat) تمتاز بالوضوح والإنصاف: تبدأ بالتعاون، وتكافئ التعاون بالمثل، وتعاقب الخيانة فورًا، وتغفر سريعًا إذا عاد الطرف الآخر للتعاون، مما يثبت صحة الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Tit-for-Tat balances niceness, prompt retaliation, forgiveness, and transparency, mathematically dominating competitive simulations (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 10. Constructive Feedback Delivery (Praise-Critique-Praise Alignment)
  {
    id: 'social_feedback_delivery_sandwich',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'فصل الملاحظات وتوجيه النقد لسلوك محدد قابل للقياس دون تجريح شخصي، مع الاتفاق على خطوات تصحيحية واضحة', text_en: 'Focus objectively on specific measurable behavior without personal attacks, co-creating actionable corrective steps', isCorrect: true },
        { id: 'B', text_ar: 'مهاجمة ذكاء الموظف وشخصيته في بريد إلكتروني جماعي', text_en: 'Attack the employee\'s intelligence on a group email', isCorrect: false },
        { id: 'C', text_ar: 'الامتناع عن التوجيه تمامًا واستبدال الموظف سرًا', text_en: 'Avoid giving feedback and secretly replace the staff', isCorrect: false },
        { id: 'D', text_ar: 'تقديم مجاملات كاذبة وإخفاء كل الأخطاء لعدم إزعاجه', text_en: 'Offer fake flattery and conceal all mistakes', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `عند تقديم تغذية راجعة تصحيحية حرجة لموظف ارتكب خطأً تقنيًا فادحًا:\nما هو النهج الأكثر فعالية لضمان تصحيح الخطأ والحفاظ على الأمان النفسي والدافعية المهنية؟`,
        questionText_en: `When delivering critical performance feedback to a team member who made a serious error:\nWhat leadership approach best secures behavioral correction while sustaining psychological safety?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `التغذية الراجعة الفعالة تركز على "السلوك والنتيجة" (Behavior and Impact) وليس على هوية الشخص، وتفتح حوارًا لحل المشكلة مستقبليًا، وهو ما يطابق الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Decoupling identity from actionable behavior creates psychological safety, focusing cognition on constructive problem-solving (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 11. Cross-Cultural High vs Low Context Communication
  {
    id: 'social_cross_cultural_pragmatics',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'ثقافة تواصل عالية السياق (High-Context Culture) تعتمد على التلميح غير اللفظي وحفظ ماء الوجه', text_en: 'High-Context Culture relying on non-verbal nuance and face-saving politeness', isCorrect: true },
        { id: 'B', text_ar: 'ثقافة منخفضة السياق (Low-Context) صريحة ومباشرة بنسبة 100%', text_en: 'Low-Context Culture characterized by blunt literal directness', isCorrect: false },
        { id: 'C', text_ar: 'موقف يعبر عن موافقة تعاقدية ملزمة قانونيًا فورًا', text_en: 'A binding legal agreement', isCorrect: false },
        { id: 'D', text_ar: 'عدم فهم المترجم للغة الإنجليزية', text_en: 'Translational incompetence', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في مفاوضات دولية، عندما عُرض اقتراح معين، ابتسم المفاوض الآسيوي وقال بهدوء: "هذا مقترح مثير للاهتمام، لكنه قد يواجه بعض التحديات الدقيقة التي تتطلب الكثير من التأمل".\nما هو التفسير الاجتماعي الثقافي الدقيق لهذه العبارة؟`,
        questionText_en: `In cross-cultural negotiation (Edward T. Hall framework):\nA counterpart smiles and softly remarks: "This is an interesting proposal, though it may present subtle challenges requiring contemplation."\nWhat is the authentic intercultural pragmatic interpretation?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `في الثقافات عالية السياق (High-Context)، يتم تجنب الرفض الفج الصريح للحفاظ على الانسجام وماء الوجه، وهذه العبارة المهذبة تعني في جوهرها الرفض أو التحفظ الجذري على المقترح، وهو ما يحدده الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `High-context communication relies on polite euphemistic understatements to convey rejection without loss of face, confirming (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 12. Gaslighting & Epistemic Invalidation Detection
  {
    id: 'social_gaslighting_detection',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'التلاعب بالواقع والتشكيك في الإدراك (Gaslighting / Epistemic Invalidation)', text_en: 'Gaslighting / Epistemic Invalidation', isCorrect: true },
        { id: 'B', text_ar: 'التفاوض العقلاني القائم على الأدلة', text_en: 'Rational evidence-based negotiation', isCorrect: false },
        { id: 'C', text_ar: 'التعاطف الوجداني الصادق', text_en: 'Sincere affective empathy', isCorrect: false },
        { id: 'D', text_ar: 'إدارة الوقت بصرامة', text_en: 'Strict time management', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `عندما تواجه شريك عمل برسائل بريد إلكتروني مثبتة تبيّن مخالفته للاتفاق، فيرد عليك باستهزاء: "أنت تتخيل أشياء لم تحدث، وذاكرتك ضعيفة ومضطربة، ولم نتفق على هذا إطلاقًا!".\nما هو التكتيك النفسي التلاعبي الممارس هنا في علم النفس الاجتماعي؟`,
        questionText_en: `When presented with documented email evidence of an agreement, a peer responds: "You are imagining things, your memory is completely unstable, and we never agreed to that!"\nWhat psychological manipulative tactic is being deployed?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `التلاعب بالعقول (Gaslighting) هو تكتيك تلاعبي يهدف إلى جعل الضحية تشك في سلامة عقلها وإدراكها وذاكرتها من خلال الإنكار المتعمد للحقائق الثابتة، وهو ما يحدده الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Gaslighting systematically undermines the target's confidence in their own memory and perception through blatant denial of factual reality (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 13. Ethical Crisis Triage Prioritization
  {
    id: 'social_sjt_crisis_triage',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'اتباع بروتوكول الفرز الطبي الموضوعي المعتمد على الأولوية السريرية وفرص النجاة المثبتة علميًا', text_en: 'Adhere to objective clinical triage protocols prioritizing survivability and acute severity', isCorrect: true },
        { id: 'B', text_ar: 'علاج الشخص الأكثر ثراءً ونفوذًا اجتماعيًا أولاً', text_en: 'Prioritize the wealthiest or most socially influential individual', isCorrect: false },
        { id: 'C', text_ar: 'إجراء قرعة عشوائية بالعملة المعدنية وتجاهل الحالة الطبية', text_en: 'Flip a coin and ignore clinical criteria', isCorrect: false },
        { id: 'D', text_ar: 'الامتناع عن علاج أي مصاب حتى وصول لجان إدارية عليا', text_en: 'Withhold care from all patients until bureaucratic committees arrive', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في كارثة طارئة مع شح حاد في الموارد الطبية وسيارات الإسعاف:\nما هو المعيار الأخلاقي المهني الأكثر عدالة ورسوخًا لاتخاذ قرار أولوية إسعاف المصابين في علم النفس الاجتماعي وإدارة الأزمات؟`,
        questionText_en: `In emergency triage resource allocation under acute deficit:\nWhat is the most ethically robust and psychometrically aligned principle governing crisis prioritization?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `أخلاقيات الطوارئ والفرز الطبي ترتكز على معايير سريرية محايدة تهدف إلى تعظيم إنقاذ الأرواح بناءً على شدة الإصابة وقابلية النجاة، بعيدًا عن أي اعتبارات طبقية أو عشوائية، وهو ما يثبت صحة الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Standard ethical triage relies strictly on objective clinical severity and survivability metrics to maximize life-saving utility (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 14. Ethical Dilemma: Utilitarian vs Deontological Balance
  {
    id: 'social_ethical_dilemma_utilitarian',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'المعضلة الأخلاقية تمثل صراعًا كلاسيكيًا بين مذهب المنفعة العواقبية (النتائج) ومذهب الواجب الأخلاقي (المبادئ)', text_en: 'The dilemma embodies the classic tension between Utilitarian consequentialism and Deontological duty ethics', isCorrect: true },
        { id: 'B', text_ar: 'الموقف بسيط وحله بديهي دون أي بعد فلسفي', text_en: 'The scenario is trivial with no philosophical depth', isCorrect: false },
        { id: 'C', text_ar: 'الأخلاق تتطلب دائمًا تجاهل حياة البشر تمامًا', text_en: 'Ethics mandates ignoring human life', isCorrect: false },
        { id: 'D', text_ar: 'لا توجد أي قواعد أخلاقية يمكن تطبيقها', text_en: 'Zero ethical frameworks apply', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في تجربة عربة القطار الفلسفية الشهيرة (The Trolley Problem):\nحيث يمكن تحويل مسار قطار لإنقاذ 5 عمال مقابل التضحية بعامل واحد على المسار البديل.\nما هو التحليل الفلسفي الإدراكي الصحيح لطبيعة هذا الصراع الأخلاقي؟`,
        questionText_en: `In the classic Trolley Problem moral dilemma:\nSwitching the track saves five workers while sacrificing one worker on the side track.\nWhat is the accurate meta-ethical cognitive formulation of this conflict?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `معضلة العربة تجسد التنافس الفلسفي بين النفعية (تعظيم النتيجة لإنقاذ أكبر عدد) والواجب الأخلاقي الكانطي (عدم استخدام الإنسان كوسيلة بل كغاية بحد ذاته)، وهو ما يمثله الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `The paradigm illustrates the foundational meta-ethical conflict between consequentialist utilitarian optimization and deontological moral imperatives (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 15. Whistleblower Escalation & Ethical Loyalty
  {
    id: 'social_whistleblower_escalation',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'توثيق الأدلة بمهنية وإبلاغ قنوات الامتثال الرقابية المعتمدة رسميًا لحماية المصلحة العامة', text_en: 'Professionally document evidence and escalate via official regulatory compliance channels to protect public safety', isCorrect: true },
        { id: 'B', text_ar: 'المشاركة في التزوير لكسب رضا المدير والحصول على ترقية', text_en: 'Participate in the fraud to curry favor with management', isCorrect: false },
        { id: 'C', text_ar: 'تدمير الأدلة وحذف كل السجلات للتهرب من المسؤولية', text_en: 'Destroy evidence and erase records', isCorrect: false },
        { id: 'D', text_ar: 'ابتزاز الإدارة طلبًا لرشوة شخصية مقابل الصمت', text_en: 'Blackmail management for personal bribes in exchange for silence', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `اكتشف مهندس وجود خلل تصنيعي خطير في نظام مكابح السيارات التي تنتجها شركته يهدد أرواح المستهلكين، ورفضت الإدارة المباشرة معالجة الأمر بدافع تقليل التكاليف.\nما هو المسار الأخلاقي والقانوني الصارم الذي يجب أن يسلكه المهندس وفق مبادئ الإبلاغ الأخلاقي (Whistleblowing)؟`,
        questionText_en: `An engineer discovers a critical manufacturing defect in braking systems endangering lives, but immediate management suppresses the report to cut costs.\nWhat is the rigorous ethical and professional escalation path under ethical whistleblowing principles?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `المسؤولية الأخلاقية والمهنية العليا للمهندس تقتضي تقديم حماية الأرواح والسلامة العامة فوق أي ولاء تنفيذي غير نزيه، وذلك عبر توثيق الأدلة ورفعها لقنوات الامتثال والجهات الرقابية، وهو ما يطابق الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Professional engineering ethics prioritizes public safety over corporate complicity, mandating documented escalation via compliance oversight channels (${shuffled[correctIndex].id}).`
      };
    }
  }
];

